import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { theme } from '../../../../core/design-system/theme'
import { useBookingContext, BookingStepShell } from '../BookingFlowView'

const labelStyle = {
  fontSize: 11,
  fontWeight: 600,
  color: theme.colors.textSecondary,
  display: 'block',
  marginBottom: 5,
  fontFamily: 'var(--font-body)',
  letterSpacing: '0.04em',
}

const baseInputStyle = {
  background: theme.colors.card,
  border: `1px solid ${theme.colors.border}`,
  borderRadius: theme.layout.inputRadius,
  padding: 12,
  fontSize: 15,
  color: theme.colors.textPrimary,
  fontFamily: 'var(--font-body)',
  width: '100%',
  boxSizing: 'border-box',
  outline: 'none',
}

export default function BookingStep2View() {
  const { viewModel } = useBookingContext()
  const navigate = useNavigate()

  const [children, setChildren] = useState(viewModel.draft.children ?? [])
  const [childName, setChildName] = useState('')
  const [childAge, setChildAge] = useState('')
  const [childNotes, setChildNotes] = useState('')
  const [focusedField, setFocused] = useState(null)

  const canAddChild =
    childName.trim().length > 0 && childAge !== '' && Number(childAge) >= 0
  const canContinue = children.length > 0

  function handleAddChild() {
    if (!canAddChild) return
    const newChild = {
      id: crypto.randomUUID(),
      fullName: childName.trim(),
      ageYears: Number(childAge),
      notes: childNotes.trim() || null,
    }
    setChildren((prev) => [...prev, newChild])
    setChildName('')
    setChildAge('')
    setChildNotes('')
  }

  function handleRemoveChild(id) {
    setChildren((prev) => prev.filter((c) => c.id !== id))
  }

  function handleContinue() {
    if (!canContinue) return
    viewModel.setChildren(children)
    navigate('/book/datetime')
  }

  function inputStyle(field) {
    return {
      ...baseInputStyle,
      outline: focusedField === field ? `2px solid ${theme.colors.terracotta}` : 'none',
    }
  }

  return (
    <BookingStepShell
      step={2}
      title="Children joining you"
      onBack={() => navigate(-1)}
      onContinue={handleContinue}
      continueDisabled={!canContinue}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Add child form */}
        <div
          style={{
            background: theme.colors.card,
            border: `1px solid ${theme.colors.border}`,
            borderRadius: theme.layout.cardRadius,
            padding: 16,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label htmlFor="childName" style={labelStyle}>
                CHILD'S NAME
              </label>
              <input
                id="childName"
                type="text"
                value={childName}
                onChange={(e) => setChildName(e.target.value)}
                placeholder="Emma"
                style={inputStyle('childName')}
                onFocus={() => setFocused('childName')}
                onBlur={() => setFocused(null)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddChild()}
              />
            </div>

            <div>
              <label htmlFor="childAge" style={labelStyle}>
                AGE (YEARS)
              </label>
              <input
                id="childAge"
                type="number"
                min="0"
                max="17"
                value={childAge}
                onChange={(e) => setChildAge(e.target.value)}
                placeholder="3"
                style={inputStyle('childAge')}
                onFocus={() => setFocused('childAge')}
                onBlur={() => setFocused(null)}
              />
            </div>

            <div>
              <label htmlFor="childNotes" style={labelStyle}>
                NOTES (OPTIONAL)
              </label>
              <input
                id="childNotes"
                type="text"
                value={childNotes}
                onChange={(e) => setChildNotes(e.target.value)}
                placeholder="Allergies, preferences…"
                style={inputStyle('childNotes')}
                onFocus={() => setFocused('childNotes')}
                onBlur={() => setFocused(null)}
              />
            </div>

            <button
              onClick={handleAddChild}
              disabled={!canAddChild}
              style={{
                background: 'none',
                border: `1.5px solid ${theme.colors.terracotta}`,
                borderRadius: theme.layout.buttonRadius,
                padding: '10px 16px',
                color: theme.colors.terracotta,
                fontSize: 14,
                fontWeight: 600,
                fontFamily: 'var(--font-body)',
                cursor: canAddChild ? 'pointer' : 'not-allowed',
                opacity: canAddChild ? 1 : 0.4,
              }}
            >
              + Add child
            </button>
          </div>
        </div>

        {/* Empty state */}
        {children.length === 0 && (
          <p
            style={{
              textAlign: 'center',
              color: theme.colors.textTertiary,
              fontSize: 14,
              fontFamily: 'var(--font-body)',
              margin: '4px 0',
            }}
          >
            Add at least one child to continue.
          </p>
        )}

        {/* Children list */}
        {children.map((child) => (
          <div
            key={child.id}
            style={{
              background: theme.colors.card,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.layout.cardRadius,
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: 15,
                  color: theme.colors.textPrimary,
                  fontFamily: 'var(--font-body)',
                }}
              >
                {child.fullName}
              </div>
              <div
                style={{
                  color: theme.colors.textSecondary,
                  fontSize: 13,
                  fontFamily: 'var(--font-body)',
                  marginTop: 2,
                }}
              >
                {child.ageYears === 1 ? '1 year old' : `${child.ageYears} years old`}
                {child.notes ? ` · ${child.notes}` : ''}
              </div>
            </div>
            <button
              onClick={() => handleRemoveChild(child.id)}
              aria-label={`Remove ${child.fullName}`}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: theme.colors.textTertiary,
                padding: 6,
                lineHeight: 0,
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        ))}
      </div>
    </BookingStepShell>
  )
}
